/**
 * SLAModern - MIGRATED to use CRUDPage template
 * 
 * Before: 346 lines
 * After: ~120 lines (65% reduction!)
 * 
 * Manages Service Level Agreement (SLA) configurations for task priorities.
 * Defines target time (hours + minutes) for different priority levels.
 */

import React from 'react';
import { Box, Chip } from '@mui/material';
import { Clock } from 'lucide-react';

import CRUDPage from '../Components/UI/CRUDPage';

// Field definitions
const slaFields = [
  {
    name: 'priority',
    label: 'Priority',
    type: 'select',
    required: true,
    validation: {
      required: 'Priority is required'
    },
    options: [
      { value: 'critical', label: 'Critical' },
      { value: 'high', label: 'High' },
      { value: 'medium', label: 'Medium' },
      { value: 'low', label: 'Low' }
    ]
  },
  {
    name: 'sla_hours',
    label: 'Target Time Hours',
    type: 'number',
    validation: {
      min: { value: 0, message: 'Hours must be positive' }
    }
  },
  {
    name: 'sla_minutes',
    label: 'Target Time Minutes',
    type: 'number',
    validation: {
      min: { value: 0, message: 'Minutes must be 0 or greater' },
      max: { value: 59, message: 'Minutes must be less than 60' }
    }
  },
  {
    name: 'description',
    label: 'Description',
    type: 'text',
    multiline: true,
    rows: 3
  }
];

// Helper function to format time interval from PostgreSQL format
const formatTimeInterval = (timingString) => {
  if (!timingString) return 'N/A';
  
  // Handle PostgreSQL INTERVAL format: "hh:mm:ss" or "X days hh:mm:ss"
  const match = timingString.match(/(?:(\d+)\s+days?\s+)?(\d+):(\d+):/);
  if (match) {
    const days = parseInt(match[1] || 0, 10);
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);

    return parts.length > 0 ? parts.join(' ') : '0m';
  }
  
  return timingString;
};

// Helper function to get priority color
const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'error';
    case 'high': return 'warning';
    case 'medium': return 'info';
    case 'low': return 'success';
    default: return 'default';
  }
};

// Column definitions
const columns = [
  {
    field: 'priority',
    headerName: 'Priority',
    sortable: true,
    renderCell: ({ value }) => (
      <Chip
        label={value ? value.charAt(0).toUpperCase() + value.slice(1) : 'N/A'}
        size="small"
        color={getPriorityColor(value)}
        variant="filled"
        sx={{ fontWeight: 500, minWidth: '80px' }}
      />
    )
  },
  {
    field: 'sla_timing',
    headerName: 'Target Time',
    sortable: true,
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Clock size={16} color="#4880ff" />
        <Chip
          label={formatTimeInterval(value)}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>
    )
  },
  {
    field: 'description',
    headerName: 'Description',
    sortable: false,
    renderCell: ({ value }) => (
      <span style={{ color: 'text.secondary' }}>
        {value || 'No description'}
      </span>
    )
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    align: 'right'
  }
];

function SLAModern() {
  return (
    <CRUDPage
      title="Service Level Agreement (SLA) Management"
      subtitle="Define target response and resolution times for different priority levels"
      icon={Clock}
      columns={columns}
      fields={slaFields}
      searchFields={['priority', 'description']}
      permissions={{
        resource: 'Target Time',
        module: 'slaConfig'
      }}
      crudConfig={{
        endpoint: '/api/sla-master',
        entityName: 'SLA',
        entityNamePlural: 'SLAs',
        useOrgId: true,
      }}
      emptyMessage="No SLAs found. Create your first SLA to define service response and resolution times."
    />
  );
}

export default SLAModern;
